import { ElementRef, inject, Injectable } from '@angular/core';
import { concatMap, from, map, Observable, toArray } from 'rxjs';
import { downloadTripInfoConfig } from '../models/download-pdf.model';
import { HTML2CanvasService } from './html2canvas.service';
import { JsPDFService } from './jspdf.service';

@Injectable({ providedIn: 'root' })
export class DownloadPdfService {
  private readonly _html2canvasService = inject(HTML2CanvasService);
  private readonly _jsPDFService = inject(JsPDFService);

  public downloadTripInfo(htmlElements: ElementRef<HTMLDivElement>[], pnr: string): Observable<void> {
    const fileName = pnr;
    return this.download(htmlElements, fileName);
  }

  private download(htmlElements: ElementRef<HTMLDivElement>[], fileName: string): Observable<void> {
    const pdf = this._jsPDFService.create({ orientation: 'p', unit: 'mm' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    return from(htmlElements).pipe(
      concatMap((element) =>
        this._html2canvasService
          .html2Canvas(element.nativeElement, {
            scale: 1,
            windowWidth: element.nativeElement.scrollWidth,
            windowHeight: element.nativeElement.scrollHeight
          })
          .pipe(
            map((canvas) => ({
              canvas,
              widthPx: canvas.width,
              heightPx: canvas.height
            }))
          )
      ),
      toArray(),
      map((images) => {
        let remainingPageHeight = pageHeight;

        images.forEach(({ canvas, widthPx, heightPx }) => {
          const imgWidth = pageWidth;
          const pxPerMm = widthPx / imgWidth;
          const pageHeightPx = pageHeight * pxPerMm;

          let offsetPx = 0;

          while (offsetPx < heightPx) {
            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width = widthPx;
            sliceCanvas.height = Math.min(pageHeightPx, heightPx - offsetPx);

            const ctx = sliceCanvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(canvas, 0, offsetPx, widthPx, sliceCanvas.height, 0, 0, widthPx, sliceCanvas.height);

              const sliceImgData = sliceCanvas.toDataURL(downloadTripInfoConfig.canvasFormat);
              const sliceHeightMm = (sliceCanvas.height * imgWidth) / widthPx;

              if (sliceHeightMm > remainingPageHeight) {
                this._jsPDFService.addPage(pdf);
                remainingPageHeight = pageHeight;
              }

              this._jsPDFService.addImage(
                pdf,
                sliceImgData,
                downloadTripInfoConfig.imgFormat,
                0,
                pageHeight - remainingPageHeight,
                imgWidth,
                sliceHeightMm
              );

              remainingPageHeight -= sliceHeightMm;
            }

            offsetPx += pageHeightPx;
          }
        });

        this._jsPDFService.save(pdf, fileName);
      })
    );
  }
}
